import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Box,
  BoxProps,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  ResponsiveValue,
  Text,
  useOutsideClick,
  VStack,
} from '@chakra-ui/react';
import { Search } from 'lucide-react';
import { BiTrashAlt } from 'react-icons/bi';

import { FONTS_OPACITY } from '@/constant';

export interface SearchSuggestion {
  id: string | number;
  label: string;
  value: string;
  category?: string;
  description?: string;
}

export interface SearchInputProps extends BoxProps {
  placeholder?: string;
  initialQuery?: string;
  suggestions?: SearchSuggestion[];
  onSearch?: (query: string) => void;
  onSuggestionSelect?: (suggestion: SearchSuggestion) => void;
  onQueryChange?: (query: string) => void;
  onInputFocus?: () => void;
  enableAutoComplete?: boolean;
  maxSuggestions?: number;
  minQueryLength?: number;
  debounceMs?: number;
  maxWidth?: ResponsiveValue<string>;
}

const SearchInput: React.FC<SearchInputProps> = ({
  placeholder = 'Search for contacts, reports...',
  initialQuery = '',
  suggestions = [],
  onSearch,
  onSuggestionSelect,
  onQueryChange,
  onInputFocus,
  enableAutoComplete = true,
  maxSuggestions = 10,
  minQueryLength = 1,
  debounceMs = 300,
  maxWidth,
  ...props
}) => {
  const [query, setQuery] = useState(initialQuery || '');
  const [filteredSuggestions, setFilteredSuggestions] = useState<
    SearchSuggestion[]
  >([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const inputPaddingRight = useMemo(() => {
    return query.length > 0 ? 8 : 2.5;
  }, [query]);

  // Filter suggestions based on query
  const filterSuggestions = useCallback(
    (searchQuery: string) => {
      if (
        !enableAutoComplete ||
        !searchQuery ||
        searchQuery.length < minQueryLength
      ) {
        setFilteredSuggestions([]);
        return;
      }

      const filtered = suggestions
        .filter(
          suggestion =>
            suggestion.label
              .toLowerCase()
              .includes(searchQuery.toLowerCase()) ||
            suggestion.value
              .toLowerCase()
              .includes(searchQuery.toLowerCase()) ||
            (suggestion.description &&
              suggestion.description
                .toLowerCase()
                .includes(searchQuery.toLowerCase()))
        )
        .slice(0, maxSuggestions);

      setFilteredSuggestions(filtered);
    },
    [suggestions, enableAutoComplete, minQueryLength, maxSuggestions]
  );

  // Debounced search
  const debouncedSearch = useCallback(
    (searchQuery: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        filterSuggestions(searchQuery);
        onQueryChange?.(searchQuery);
      }, debounceMs);
    },
    [filterSuggestions, onQueryChange, debounceMs]
  );

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setSelectedIndex(-1);

    debouncedSearch(value);
    if (value) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
      setFilteredSuggestions([]);
    }
  };

  const handleClear = () => {
    setQuery('');
    onQueryChange?.('');
    setFilteredSuggestions([]);
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.blur();
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion: SearchSuggestion) => {
    setQuery(suggestion.label);
    setIsOpen(false);
    setSelectedIndex(-1);
    onSuggestionSelect?.(suggestion);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || filteredSuggestions.length === 0) {
      if (e.key === 'Enter') {
        onSearch?.(query);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < filteredSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev > 0 ? prev - 1 : filteredSuggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < filteredSuggestions.length) {
          handleSuggestionSelect(filteredSuggestions[selectedIndex]);
        } else {
          onSearch?.(query);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Handle input focus
  const handleFocus = () => {
    onInputFocus?.();
    if (query && filteredSuggestions.length > 0) {
      setIsOpen(true);
    }
  };

  // Close suggestions when clicking outside
  useOutsideClick({
    ref: containerRef,
    handler: () => setIsOpen(false),
  });

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <Box
      position='relative'
      flex={1}
      width='full'
      maxWidth={maxWidth}
      ref={containerRef}
      {...props}
    >
      <InputGroup size='sm'>
        <InputLeftElement pointerEvents='none' color='search-input.icon'>
          <Search size={16} />
        </InputLeftElement>
        <Input
          bg='search-input.bg'
          color='search-input.text'
          borderColor='search-input.border!'
          _groupHover={{
            borderColor: 'search-input.border-hover!',
            _disabled: {
              borderColor: 'search-input.border-disabled!',
            },
          }}
          _hover={{
            bg: 'search-input.bg-hover',
            color: 'search-input.text-hover',
            borderColor: 'search-input.border-hover!',
          }}
          _focus={{
            bg: 'search-input.bg-active',
            color: 'search-input.text-active',
            borderColor: 'search-input.border-active!',
            _hover: {
              bg: 'search-input.bg-hover-filled',
              color: 'search-input.text-hover-filled',
              borderColor: 'search-input.border-hover-filled!',
            },
          }}
          _disabled={{
            bg: 'search-input.bg-disabled!',
            color: 'search-input.text-disabled!',
            borderColor: 'search-input.border-disabled!',
          }}
          ref={inputRef}
          variant='outline'
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          paddingRight={inputPaddingRight}
          className='peer'
          required
          minLength={1}
        />
        {query.length > 0 && (
          <InputRightElement
            cursor='pointer'
            onClick={handleClear}
            color='search-input.icon'
          >
            <BiTrashAlt size={16} />
          </InputRightElement>
        )}
      </InputGroup>

      {/* Suggestions Dropdown */}
      {isOpen && enableAutoComplete && filteredSuggestions.length > 0 && (
        <Box
          position='absolute'
          top='100%'
          left={0}
          right={0}
          zIndex='tooltip'
          mt={1}
          bg='cards.bg'
          border='1px'
          borderColor='border.light'
          borderRadius='md'
          boxShadow='lg'
          maxH='60'
          overflowY='auto'
        >
          <VStack spacing={0} align='stretch'>
            {filteredSuggestions.map((suggestion, index) => (
              <Box
                key={suggestion.id}
                px={4}
                py={3}
                cursor='pointer'
                bg={selectedIndex === index ? 'cards.bg-hover' : 'transparent'}
                _hover={{ bg: 'cards.bg-hover' }}
                onClick={() => handleSuggestionSelect(suggestion)}
                borderBottom={
                  index < filteredSuggestions.length - 1 ? '1px' : 'none'
                }
                borderBottomColor='border.light'
              >
                <HStack justify='space-between' align='start'>
                  <VStack align='start' spacing={1} flex={1}>
                    <Text size='secondarySemiBold'>{suggestion.label}</Text>
                    {suggestion.description && (
                      <Text size='notePrimaryRegular' opacity={FONTS_OPACITY}>
                        {suggestion.description}
                      </Text>
                    )}
                  </VStack>
                  {suggestion.category && (
                    <Text
                      size='notePrimaryRegular'
                      color='accent.450'
                      paddingX={2}
                      paddingY={1}
                      bg='accent.900'
                      rounded='sm'
                    >
                      {suggestion.category}
                    </Text>
                  )}
                </HStack>
              </Box>
            ))}
          </VStack>
        </Box>
      )}
    </Box>
  );
};

export default SearchInput;
